import { portfolioSystemPrompt } from "./aiPortfolioContext";

const XAI_API_KEY =
  "xai-HMjLGayhpI3K4DvRpzjBqSb1oCU6snXZRSAxLKqHW9xt4Y4Xu5XU0ylMt5DS4sfNrnaGny2HWNQaRPAL";
const XAI_RESPONSES_URL =
  "https://api.x.ai/v1/responses";
const XAI_MODEL = "grok-4.20-reasoning";

export type VarcelChatRole = "user" | "assistant" | "system";

export type VarcelChatMessage = {
  role: VarcelChatRole;
  content: string;
};

export type StreamCareerChatOptions = {
  messages: VarcelChatMessage[];
  onToken?: (token: string) => void;
  signal?: AbortSignal;
};

const getResponseText = (response: any): string => {
  if (typeof response?.output_text === "string") {
    return response.output_text;
  }

  if (!Array.isArray(response?.output)) {
    return "";
  }

  return response.output
    .flatMap((item: any) => item?.content ?? [])
    .map((content: any) => content?.text ?? "")
    .join("");
};

const getMessageText = (message: VarcelChatMessage) =>
  `${message.role.toUpperCase()}: ${message.content.trim()}`;

const buildResponsesInput = (messages: VarcelChatMessage[]) => {
  const conversation = messages
    .filter((message) => message.content.trim())
    .map(getMessageText)
    .join("\n\n");

  return `${portfolioSystemPrompt.trim()}\n\nCONVERSATION:\n${conversation}`;
};

const parseStreamLine = (line: string) => {
  const message = line.replace(/^(data|event):\s*/, "").trim();

  if (!message || message === "[DONE]") {
    return null;
  }

  try {
    const parsed = JSON.parse(message);
    const choice = parsed.choices?.[0];
    return (
      parsed.delta ??
      parsed.text ??
      parsed.output_text ??
      choice?.delta?.content ??
      choice?.message?.content ??
      null
    );
  } catch (error) {
    console.warn("Unable to parse xAI stream chunk:", error);
    return null;
  }
};

export class VarcelSDK {
  async streamCareerChat({
    messages,
    onToken,
    signal,
  }: StreamCareerChatOptions): Promise<string> {
    if (!XAI_API_KEY) {
      throw new Error("Missing xAI API key.");
    }

    const response = await fetch(XAI_RESPONSES_URL, {
      method: "POST",
      signal,
      headers: {
        Authorization: `Bearer ${XAI_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: XAI_MODEL,
        input: buildResponsesInput(messages),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`xAI response failed: ${response.status} ${errorText}`);
    }

    if (!response.body) {
      const data = await response.json();
      const text = getResponseText(data);
      onToken?.(text);
      return text;
    }

    const contentType = response.headers.get("Content-Type") ?? "";
    if (!contentType.includes("text/event-stream")) {
      const data = await response.json();
      const text = getResponseText(data);
      onToken?.(text);
      return text;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = "";
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data:")) {
          continue;
        }

        const token = parseStreamLine(line);

        if (token) {
          fullResponse += token;
          onToken?.(token);
        }
      }
    }

    return fullResponse;
  }
}

export const varcelSDK = new VarcelSDK();
